import React from 'react';
import { useIntl } from 'react-intl';
import { firstValueFrom } from 'rxjs';
import type { RegisterMessageData } from '@dh-fe/react-support-chat';
import Loading from 'components/loading';
import useReadonlyObservable from 'enhancers/use-readonly-observable';
import useToast from 'enhancers/use-toast';
import authentication from 'services/authentication';
import onlineSupportConfigStore from 'store/online-support-config';
import onlineSupportService from 'services/online-support';
import { noopFn } from 'utils/noop';

import { toastIds } from './support-chat.settings';
import messages from './support-chat.i18n';
import UI from './support-chat.ui';

interface SupportChatElement {
  onKicked: () => void;
}

const SupportChat: React.FunctionComponent<SupportChatElement> = (props) => {
  const { onKicked } = props;
  const [onlineSupport] = useReadonlyObservable(
    onlineSupportConfigStore.onChange$,
    onlineSupportConfigStore.initialState
  );
  const toast = useToast();
  const { formatMessage } = useIntl();
  const { socketUrl } = onlineSupport || {};
  const translations = messages.pages.landing.onlineSupport.supportChat;

  const supportChatConfig = React.useMemo(
    (): RegisterMessageData | undefined => onlineSupport?.onlineSupportConfig,
    [onlineSupport]
  );

  const handleGetAuthToken = React.useCallback(async () => {
    try {
      if (authentication.isAuthenticated()) {
        const token = await firstValueFrom(onlineSupportService.createToken$());

        return { token };
      }

      const guestNick = `guest${Math.floor(Math.random() * 1000)}`;
      const token = await firstValueFrom(onlineSupportService.createGuestToken$(guestNick));

      return { token };
    } catch (error) {
      toast.error(toastIds.unexpectedError, {
        message: formatMessage(translations.errors.unexpected),
      });

      throw error;
    }
  }, [formatMessage, toast]);

  if (!supportChatConfig || !socketUrl) return <Loading className="mt-20" />;

  return (
    <UI
      config={supportChatConfig}
      socketUrl={socketUrl}
      onGetAuthToken={handleGetAuthToken}
      onFirstSupportMessageReceived={noopFn}
      onKicked={onKicked}
    />
  );
};

export default SupportChat;
